/**
 * React.lazy wrapper that recovers from post-deploy ChunkLoadError.
 *
 * After a Firebase Hosting deploy, a tab may still run an older main bundle that
 * requests hashed chunks that no longer exist (or arrive as HTML via SPA rewrite).
 * One hard reload usually picks up the new index.html and matching chunks.
 */

import React from 'react';

export const CHUNK_RELOAD_SESSION_KEY = 'lsc_chunk_reload_ts';

/** Cooldown so a broken deploy cannot reload-loop. */
export const CHUNK_RELOAD_COOLDOWN_MS = 15000;

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isChunkLoadError(error) {
    if (!error) return false;
    const name = String(error.name || '');
    const message = String(error.message || error);
    if (name === 'ChunkLoadError') return true;
    if (/Loading chunk [\w-]+ failed/i.test(message)) return true;
    if (/Failed to fetch dynamically imported module/i.test(message)) return true;
    if (/error loading dynamically imported module/i.test(message)) return true;
    if (/Importing a module script failed/i.test(message)) return true;
    return false;
}

/**
 * @param {{ now?: number, storage?: Storage | null } } [opts]
 * @returns {boolean} true if a reload was started (or should be treated as started)
 */
export function reloadOnceOnChunkError(opts = {}) {
    const now = opts.now ?? Date.now();
    const storage =
        opts.storage !== undefined
            ? opts.storage
            : typeof sessionStorage !== 'undefined'
              ? sessionStorage
              : null;

    try {
        const lastRaw = storage?.getItem?.(CHUNK_RELOAD_SESSION_KEY);
        const last = lastRaw ? Number(lastRaw) : 0;
        if (last && Number.isFinite(last) && now - last < CHUNK_RELOAD_COOLDOWN_MS) {
            return false;
        }
        storage?.setItem?.(CHUNK_RELOAD_SESSION_KEY, String(now));
    } catch {
        // sessionStorage blocked — still attempt one reload
    }

    if (typeof window !== 'undefined' && typeof window.location?.reload === 'function') {
        window.location.reload();
    }
    return true;
}

/**
 * @template T
 * @param {() => Promise<{ default: T }>} factory
 * @returns {React.LazyExoticComponent<T>}
 */
export function lazyWithChunkReload(factory) {
    return React.lazy(() =>
        factory().catch((error) => {
            if (isChunkLoadError(error) && reloadOnceOnChunkError()) {
                // Stay pending while the page reloads so Suspense does not surface the error.
                return new Promise(() => {});
            }
            throw error;
        })
    );
}
